import { useState, useEffect, useCallback } from 'react';
import { 
  getReadNotifications, 
  markAsRead, 
  markAllAsRead, 
  formatTimeAgo, 
  groupNotificationsByTime,
  generateNotificationId 
} from '../utils/notifications';

/**
 * Custom hook for managing notifications
 * Industry-standard frontend-only notification system
 */
export const useNotifications = (pickups = []) => {
  const [notifications, setNotifications] = useState([]);
  const [readNotifications, setReadNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load read notifications from localStorage
  const loadReadNotifications = useCallback(async () => {
    const read = await getReadNotifications();
    setReadNotifications(read);
    return read;
  }, []);

  // Generate notifications from pickups
  const generateNotifications = useCallback(async (pickupList, readList) => {
    const notificationsList = [];

    // Get all pickups with status PICKER ASSIGNED
    const assignedPickups = pickupList
      .filter(p => p.status === 'PICKER ASSIGNED')
      .sort((a, b) => {
        // Sort by created_at or order_id (newest first)
        const aTime = a.created_at ? new Date(a.created_at).getTime() : (a.order_id || 0);
        const bTime = b.created_at ? new Date(b.created_at).getTime() : (b.order_id || 0);
        return bTime - aTime;
      });

    assignedPickups.forEach((pickup) => {
      const notificationId = generateNotificationId(pickup.id, 'pickup');
      const isRead = readList.includes(notificationId);
      
      // Use created_at timestamp if available, otherwise use current time for new pickups
      const timestamp = pickup.created_at 
        ? new Date(pickup.created_at).getTime() 
        : Date.now();

      notificationsList.push({
        id: notificationId,
        type: 'pickup',
        title: 'New Pickup Assigned',
        message: `Pickup at ${pickup.location?.address || 'assigned location'}`,
        timestamp: timestamp,
        timeAgo: formatTimeAgo(timestamp),
        read: isRead,
        orderId: pickup.id,
        orderNumber: pickup.id
      });
    });

    // Sort by timestamp (newest first)
    notificationsList.sort((a, b) => b.timestamp - a.timestamp);

    setNotifications(notificationsList);
    setIsLoading(false);
  }, []);

  // Initialize notifications
  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      const read = await loadReadNotifications();
      await generateNotifications(pickups, read);
    };

    if (pickups.length >= 0) {
      initialize();
    }
  }, [pickups, loadReadNotifications, generateNotifications]);

  // Update read status when readNotifications change
  useEffect(() => {
    setNotifications(prev => 
      prev.map(notif => ({
        ...notif,
        read: readNotifications.includes(notif.id),
        timeAgo: formatTimeAgo(notif.timestamp)
      }))
    );
  }, [readNotifications]);

  // Mark notification as read
  const handleMarkAsRead = useCallback(async (notificationId) => {
    await markAsRead(notificationId);
    const updated = await loadReadNotifications();
    setReadNotifications(updated);
  }, [loadReadNotifications]);

  // Mark all notifications as read
  const handleMarkAllAsRead = useCallback(async () => {
    const notificationIds = notifications.map(n => n.id);
    await markAllAsRead(notificationIds);
    const updated = await loadReadNotifications();
    setReadNotifications(updated);
  }, [notifications, loadReadNotifications]);

  // Get unread count
  const unreadCount = notifications.filter(n => !n.read).length;

  // Get grouped notifications
  const groupedNotifications = groupNotificationsByTime(notifications);

  return {
    notifications,
    groupedNotifications,
    unreadCount,
    isLoading,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    refresh: () => loadReadNotifications()
  };
};

